import { useState, useEffect, useCallback, useMemo } from 'react';
import { getZoomAPI, MockZoomAPI } from '../utils/zoomAPI';
import type { UserRole, MeetingContext, ZoomMessage } from '../types';

/**
 * useZoomApp - Hook for Zoom Apps SDK integration
 *
 * Handles:
 * - SDK initialization
 * - Role detection (host vs participant)
 * - Message handling
 * - Development mode (mock SDK)
 */

interface UseZoomAppReturn {
  isReady: boolean;
  isInitializing: boolean;
  error: Error | null;
  userRole: UserRole;
  meetingContext: MeetingContext | null;
  sendMessage: (message: ZoomMessage) => Promise<void>;
  sendToOtherInstances: (message: ZoomMessage) => Promise<void>;
  onMessage: (handler: (message: ZoomMessage) => void) => () => void;
  inviteAllParticipants: () => Promise<void>;
  expandToFullscreen: () => Promise<void>;
  sendAppToBackground: () => Promise<void>;
  bringAppToFront: () => Promise<void>;
  collapseToSidebar: () => Promise<void>;
  getMeetingParticipantCount: () => Promise<number | null>;
  isInZoom: boolean; // true when running in actual Zoom, false in browser/dev
  runningContext: string | null; // 'inMeeting', 'inImmersive', etc.
}

// Check if running in development mode (only dev server, not preview)
// preview:dev uses --mode development but should still use real SDK for Zoom testing
// Use VITE_USE_REAL_ZOOM=true to force real Zoom SDK even in dev mode (for ngrok testing)
const useRealZoom = import.meta.env.VITE_USE_REAL_ZOOM === 'true';
const isDevelopment = import.meta.env.DEV && !useRealZoom;

// Get role from URL parameter in development (e.g., ?role=participant)
function getDevRole(): UserRole {
  if (!isDevelopment) return 'participant';
  const params = new URLSearchParams(window.location.search);
  const roleParam = params.get('role');
  return roleParam === 'participant' ? 'participant' : 'host';
}

export function useZoomApp(): UseZoomAppReturn {
  const [isReady, setIsReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('participant');
  const [meetingContext, setMeetingContext] = useState<MeetingContext | null>(null);
  const [runningContext, setRunningContext] = useState<string | null>(null);

  // Use mock API in development - memoize to prevent creating new instances
  const zoomAPI = useMemo(() => {
    const api = isDevelopment ? new MockZoomAPI() : getZoomAPI();

    // Set mock role in development
    if (isDevelopment && api instanceof MockZoomAPI) {
      api.setMockRole(getDevRole());
    }

    return api;
  }, []); // Empty deps - create once and never change

  useEffect(() => {
    let mounted = true;

    const initializeZoom = async () => {
      try {
        setIsInitializing(true);
        setError(null);

        // Initialize SDK
        await zoomAPI.initialize();

        if (!mounted) return;

        // Get meeting context
        const context = zoomAPI.getMeetingContext();
        setMeetingContext(context);

        // Determine user role
        const role = zoomAPI.getUserRole();
        setUserRole(role);

        // Get initial running context
        const runContext = await zoomAPI.getRunningContext();
        setRunningContext(runContext?.context || 'inMeeting');
        console.log('Initial running context:', runContext);

        setIsReady(true);
        console.log(`Zoom app initialized. Role: ${role}`, context);
      } catch (err) {
        if (!mounted) return;

        const error = err instanceof Error ? err : new Error('Failed to initialize Zoom SDK');
        setError(error);
        console.error('Zoom initialization error:', error);

        // In development, continue with mock data
        if (isDevelopment) {
          console.warn('Using mock Zoom SDK in development mode');
          setUserRole('host');
          setMeetingContext({
            role: 'host',
            participantId: 'dev-participant',
            meetingId: 'dev-meeting',
          });
          setRunningContext('inMeeting');
          setIsReady(true);
        }
      } finally {
        if (mounted) {
          setIsInitializing(false);
        }
      }
    };

    initializeZoom();

    // Poll running context to detect when we enter/exit immersive mode
    const intervalId = setInterval(async () => {
      const runContext = await zoomAPI.getRunningContext();
      const context = runContext?.context || 'inMeeting';
      setRunningContext(context);
    }, 500);

    return () => {
      mounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const sendMessage = useCallback(async (message: ZoomMessage): Promise<void> => {
    if (!isReady) {
      throw new Error('Zoom SDK not ready');
    }
    await zoomAPI.broadcastMessage(message);
  }, [isReady, zoomAPI]);

  const sendToOtherInstances = useCallback(async (message: ZoomMessage): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot send to other instances');
      return;
    }
    await zoomAPI.sendToOtherInstances(message);
  }, [isReady, zoomAPI]);

  const onMessage = useCallback((handler: (message: ZoomMessage) => void): (() => void) => {
    return zoomAPI.onMessage(handler);
  }, [zoomAPI]);

  const inviteAllParticipants = useCallback(async (): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot send invitation');
      return;
    }
    await zoomAPI.inviteAllParticipants();
  }, [isReady, zoomAPI]);

  const expandToFullscreen = useCallback(async (): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot expand app');
      return;
    }
    await zoomAPI.expandToFullscreen();
  }, [isReady, zoomAPI]);

  const sendAppToBackground = useCallback(async (): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot send app to background');
      return;
    }
    await zoomAPI.sendAppToBackground();
  }, [isReady, zoomAPI]);

  const bringAppToFront = useCallback(async (): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot bring app to front');
      return;
    }
    await zoomAPI.bringAppToFront();
  }, [isReady, zoomAPI]);

  const collapseToSidebar = useCallback(async (): Promise<void> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot collapse app');
      return;
    }
    await zoomAPI.collapseToSidebar();
  }, [isReady, zoomAPI]);

  const getMeetingParticipantCount = useCallback(async (): Promise<number | null> => {
    if (!isReady) {
      console.warn('Zoom SDK not ready, cannot get participant count');
      return null;
    }
    return await zoomAPI.getMeetingParticipantCount();
  }, [isReady, zoomAPI]);

  return {
    isReady,
    isInitializing,
    error,
    userRole,
    meetingContext,
    sendMessage,
    sendToOtherInstances,
    onMessage,
    inviteAllParticipants,
    expandToFullscreen,
    sendAppToBackground,
    bringAppToFront,
    collapseToSidebar,
    getMeetingParticipantCount,
    isInZoom: !isDevelopment,
    runningContext,
  };
}
